import { env } from '@noor/config';
import { dayjs } from '@noor/lib';
import { FILE_ONLY_MESSAGE } from '../config/constants';
import type { ChatMessage, ChatNote } from '../model/schema';

/**
 * Pure thread helpers (cpanel `ChatWindow` / `MessageItem`): merge notes into
 * the message stream, group by calendar day, lay out image grids, resolve file
 * URLs and build the lightbox gallery.
 */

/** A message or an operator note, tagged for the thread renderer. */
export type ThreadItem =
  | { kind: 'message'; id: string | number; createdAt: string; message: ChatMessage }
  | { kind: 'note'; id: string | number; createdAt: string; note: ChatNote };

/** One calendar day of the thread. `items` run oldest → newest. */
export interface ThreadDay {
  /** `DD.MM.YYYY` — also the React key. */
  date: string;
  items: ThreadItem[];
}

const DAY_FORMAT = 'DD.MM.YYYY';

/** `HH:mm` of a stamp, or `fallback` when it is not a valid date. */
export function formatMessageTime(createdAt: string, fallback: string): string {
  const parsed = dayjs(createdAt);
  return parsed.isValid() ? parsed.format('HH:mm') : fallback;
}

/**
 * Merge messages + notes by `created_at` and group them by day. The day groups
 * come back NEWEST day first (the thread renders in a `flex-col-reverse`
 * scroller, so the first group sits at the bottom); inside a day the items run
 * oldest → newest — cpanel's `sort(asc).reverse()` + per-group `.reverse()`.
 */
export function groupThreadByDay(
  messages: readonly ChatMessage[],
  notes: readonly ChatNote[],
): ThreadDay[] {
  const items: ThreadItem[] = [
    ...messages.map(
      (message): ThreadItem => ({
        kind: 'message',
        id: message.id,
        createdAt: message.created_at,
        message,
      }),
    ),
    ...notes.map(
      (note): ThreadItem => ({
        kind: 'note',
        id: note.id,
        createdAt: note.created_at,
        note,
      }),
    ),
  ];

  const ascending = [...items].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  const byDay = new Map<string, ThreadItem[]>();
  for (const item of [...ascending].reverse()) {
    const date = dayjs(item.createdAt).format(DAY_FORMAT);
    const bucket = byDay.get(date);
    if (bucket) bucket.push(item);
    else byDay.set(date, [item]);
  }

  return [...byDay.entries()].map(([date, dayItems]) => ({
    date,
    items: [...dayItems].reverse(),
  }));
}

const IMAGE_EXTENSION = /\.(jpeg|jpg|png|gif|bmp|webp|svg)$/i;

/** Whether an attachment path is an image (anything else renders as audio). */
export function isImageFile(file: unknown): file is string {
  return typeof file === 'string' && IMAGE_EXTENSION.test(file);
}

/**
 * Resolve an attachment path to a URL: absolute http(s) URLs pass through,
 * everything else is prefixed with the files host (cpanel `getImageUrl`).
 */
export function chatFileUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const base = (env.VITE_API_FILES_URL ?? env.VITE_API_BASE_URL).replace(/\/+$/, '');
  return `${base}/${path.replace(/^\/+/, '')}`;
}

/**
 * Whether a file-bearing message's text is the `img` placeholder cpanel sends
 * for file-only messages — such a caption carries no information.
 */
export function isPlaceholderCaption(message: ChatMessage): boolean {
  return message.files.length > 0 && message.message === FILE_ONLY_MESSAGE;
}

/**
 * Telegram-style grid rows per image count (cpanel `buildRows`): 1 → [full];
 * 2 → [½|½]; 3 → [full] + [½|½]; 4 → two rows of two; 5 → [½|½] + [⅓|⅓|⅓];
 * 6+ → rows of three. Rows hold indices into the file list.
 */
export function buildImageRows(count: number): number[][] {
  if (count <= 0) return [];
  if (count === 1) return [[0]];
  if (count === 2) return [[0, 1]];
  if (count === 3) return [[0], [1, 2]];
  if (count === 4) {
    return [
      [0, 1],
      [2, 3],
    ];
  }
  if (count === 5) {
    return [
      [0, 1],
      [2, 3, 4],
    ];
  }
  const rows: number[][] = [];
  for (let start = 0; start < count; start += 3) {
    const size = Math.min(3, count - start);
    rows.push(Array.from({ length: size }, (_, offset) => start + offset));
  }
  return rows;
}

/** One lightbox slide. */
export interface GalleryImage {
  path: string;
  messageId: string | number;
}

/**
 * Every image of the loaded thread in chronological order, for the lightbox
 * (cpanel `formattedFiles`). `startIndex` is the slide of the clicked picture
 * (`path` inside `messageId`); audio attachments are not slides.
 */
export function buildGallery(
  messages: readonly ChatMessage[],
  messageId: string | number,
  path: string,
): { images: GalleryImage[]; startIndex: number } {
  const images: GalleryImage[] = [];
  let startIndex = 0;
  const ordered = messages.toSorted((a, b) => Number(a.id) - Number(b.id));
  for (const message of ordered) {
    for (const file of message.files) {
      if (!isImageFile(file)) continue;
      if (String(message.id) === String(messageId) && file === path)
        startIndex = images.length;
      images.push({ path: file, messageId: message.id });
    }
  }
  return { images, startIndex };
}
